import { useState, useEffect } from "react"

function useLocalStorage<T>(
  key: string,
  initialValue?: T,
): [T | undefined, (value: T | undefined) => void] {
  // State to store the value
  const [storedValue, setStoredValue] = useState<T | undefined>(() => {
    try {
      const item = window.localStorage.getItem(key)

      // Parse stored json if item found, else return undefined (initialValue is now optional)
      return item ? JSON.parse(item) : initialValue
    } catch (error) {
      console.error(error)

      return initialValue
    }
  })

  useEffect(() => {
    if (
      initialValue !== undefined &&
      window.localStorage.getItem(key) === null
    ) {
      window.localStorage.setItem(key, JSON.stringify(initialValue))
    }
  }, [key, initialValue])

  const setValue = (value: T | undefined) => {
    try {
      if (value === undefined) {
        // Remove from localStorage if value is undefined
        window.localStorage.removeItem(key)
        setStoredValue(value)
      } else {
        const valueToStore =
          value instanceof Function ? value(storedValue) : value
        setStoredValue(valueToStore)
        window.localStorage.setItem(key, JSON.stringify(valueToStore))
      }
    } catch (error) {
      console.error(error)
    }
  }

  return [storedValue, setValue]
}

export default useLocalStorage
